import React, { useState, useEffect } from 'react';
import { Box, Grid, Paper, Typography, CircularProgress, Divider } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { type ILabelAndAnswerGridProps } from 'types'
import { useParams } from 'react-router-dom';
import { getUserPension } from 'service';
import { strings } from 'i18n/Strings';

export interface IUserPension {
    pensionName: string,
    accountNumber: string,
    employer: string,
    potSize: string,
    pensionCompany: string,
    isActive: string
};

const useStyles = makeStyles({
    paper: {
        width: '100%',
        margin: '10px',
    },
    title: {
        lineHeight: 1.334,
        color: '#1c3e41',
        fontWeight: 800,
        fontSize: '18px',
        textAlign: 'center',
        marginBlock: '16px',
        fontFamily: 'Inter!important',
    },
    divider: {
        borderWidth: 0,
        borderStyle: 'solid',
        borderColor: 'rgba(0, 0, 0, 0.12)',
        borderBottomWidth: 'thin',

        "@media (min-width: 900px)": {
            borderWidth: 0,
            borderStyle: 'solid',
            borderColor: 'rgba(0, 0, 0, 0.12)',
            borderBottomWidth: 0,
            height: '100%',
            borderRightWidth: 'thin',
        }
    },
    label: {
        lineHeight: '1.334',
        color: "#838383",
        fontWeight: 700,
        fontSize: '16px',
        fontFamily: 'Inter!important',
    },
    answer: {
        lineHeight: '1.334',
        color: '#1b1b1b',
        fontWeight: 700,
        fontSize: '16px',
        fontFamily: 'Inter!important',
    },
    emptyPensionContainer: {
        display: 'flex',
        justifyContent: "center",
        alignItems: "center",
        height: "70vh",
        width: "100%",
    },
    emptyPensionText: {
        fontFamily: 'Inter!important',
        color: "#838383",
        fontWeight: 500,
        fontSize: '1.125rem',
        lineHeight: 1.2,
    }
});

export const LabelAndAnswerGrid = (props: ILabelAndAnswerGridProps): JSX.Element => {
    const classes = useStyles();
    const { label, answer } = props

    return (
        <Grid container spacing={3} mx={2} mb={1} width={'80%'}>
            <Grid item xs={5} sm={5} md={5}>
                <Typography className={classes.label}>{label}</Typography>
            </Grid>
            <Grid item xs={1} sm={1} md={1}>
                <Typography>:</Typography>
            </Grid>
            <Grid item xs={5} sm={5} md={5}>
                <Typography className={classes.answer}>{answer}</Typography>
            </Grid>
        </Grid>
    )
}

const PensionAggregate = (): JSX.Element => {
    const classes = useStyles();
    const { id } = useParams();
    const [pensions, setPensions] = useState<IUserPension[] | []>([]);
    const [loader, setLoader] = useState(false);

    const getUserPensionData = async (userId: string): Promise<void> => {
        try {
            setLoader(true);
            getUserPension(userId).then(({ result }: { result: IUserPension[] | [] }) => {
                setLoader(false);
                setPensions(result);
            }).catch((err) => {
                setLoader(false);
                console.log(err);
            })
        } catch (err) {
            console.log(err);
        }
    }
    useEffect(() => {
        if (id) {
            void getUserPensionData(id);
        }
    }, [])

    return (
        <Box>
            <Paper className={classes.paper}>
                {loader ? (<Box className={classes.emptyPensionContainer}><CircularProgress /></Box>) : (
                    <>
                        {pensions.length === 0 ? (
                            <Box className={classes.emptyPensionContainer}>
                                <Typography className={classes.emptyPensionText}>{strings.displayText.pensionEmptyText}</Typography>
                            </Box>
                        ) : (
                            <Grid container spacing={3} mb={2}>
                                <Grid item xs={12} md={5.5}>
                                    <Typography className={classes.title}>{strings.displayText.activePensionTitle}</Typography>
                                    <Box display={"flex"} justifyContent={"center"} width="90%">
                                        <Divider sx={{ marginBlock: 2, width: '90%' }} />
                                    </Box>
                                    <Box sx={{ maxHeight: "calc(100vh - 200px)", overflowY: "scroll" }}>
                                        {pensions?.map((pension) => (
                                            <>
                                                {pension?.isActive && (
                                                    <>
                                                        <LabelAndAnswerGrid label={strings.displayText.pensionNameLabel} answer={pension.pensionName || "-"} />
                                                        <LabelAndAnswerGrid label={strings.displayText.accountNumberLabel} answer={pension.accountNumber || "-"} />
                                                        <LabelAndAnswerGrid label={strings.displayText.employerNameLabel} answer={pension.employer || "-"} />
                                                        <LabelAndAnswerGrid label={strings.displayText.potSizeLabel} answer={pension.potSize || "-"} />
                                                        <LabelAndAnswerGrid label={strings.displayText.pensionCompanyLabel} answer={pension.pensionCompany || "-"} />
                                                        <Box display={"flex"} justifyContent={"center"}>
                                                            <Divider sx={{ marginBlock: 2, width: '90%' }} />
                                                        </Box>
                                                    </>
                                                )}
                                            </>
                                        ))}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={0.1}><Box className={classes.divider} /></Grid>
                                <Grid item xs={12} md={5.5}>
                                    <Typography className={classes.title}>{strings.displayText.inactivepensionTitle}</Typography>
                                    <Box display={"flex"} justifyContent={"center"} width="90%">
                                        <Divider sx={{ marginBlock: 2, width: '90%' }} />
                                    </Box>
                                    <Box sx={{ maxHeight: "calc(100vh - 200px)", overflowY: "scroll" }}>
                                        {pensions?.map((pension) => (
                                            <>
                                                {!pension?.isActive && (
                                                    <>
                                                        <LabelAndAnswerGrid label={strings.displayText.pensionNameLabel} answer={pension.pensionName || "-"} />
                                                        <LabelAndAnswerGrid label={strings.displayText.accountNumberLabel} answer={pension.accountNumber || "-"} />
                                                        <LabelAndAnswerGrid label={strings.displayText.employerNameLabel} answer={pension.employer || "-"} />
                                                        <LabelAndAnswerGrid label={strings.displayText.potSizeLabel} answer={pension.potSize || "-"} />
                                                        <LabelAndAnswerGrid label={strings.displayText.pensionCompanyLabel} answer={pension.pensionCompany || "-"} />
                                                        <Box display={"flex"} justifyContent={"center"}>
                                                            <Divider sx={{ marginBlock: 2, width: '90%' }} />
                                                        </Box>
                                                    </>
                                                )}
                                            </>
                                        ))}
                                    </Box>
                                </Grid>
                            </Grid >
                        )}
                    </>
                )}
            </Paper >
        </Box >
    )
};

export default PensionAggregate;
